import React, { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../common/breadcrumbs";
import { STATUS, SWAL_SETTINGS, THEME_COLORS } from "../../../../utils/Constants";
import {
    handleServerValidations,
    hasP,
} from "../../../../utils/commonfunction";
import * as extraActionService from "../../services/extraAction.service";
import { useTranslation } from "react-i18next";
import { Placeholder, Tooltip, Whisper, Modal, Button, Form } from "rsuite";
import { useQuery, useQueryClient } from "react-query";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import moment from "moment";
import NoRowsOverlay from "../../common/NoRowsOverlay";

ModuleRegistry.registerModules([AllCommunityModule]);

const ExtraActionList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const gridRef = useRef();
    const queryClient = useQueryClient();

    const [searchData, setSearchData] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [modalData, setModalData] = useState({ title: "", shooters: 1, midcourt: 1, defenders: 1 });
    const [editMode, setEditMode] = useState(false);
    const [currentId, setCurrentId] = useState(null);

    const breadcrumbs = [
        { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
        { title: "Extra Actions", url: "" },
    ];

    const { data: listData, isLoading, refetch } = useQuery({
        queryKey: ["extra-actions-list"],
        queryFn: () => extraActionService.List(),
        refetchOnWindowFocus: false,
        retry: false,
        onError: (error) => {
            Swal.fire({
                icon: "error",
                text: handleServerValidations(error),
                ...SWAL_SETTINGS,
            });
        },
    });

    const handleAddEdit = async () => {
        try {
            let response;
            const safeNum = (v, fb = 1) => { const n = Number(v); return (v === "" || v === null || v === undefined || isNaN(n)) ? fb : n; };
            const payload = {
                title:     modalData.title,
                shooters:  safeNum(modalData.shooters,  1),
                midcourt:  safeNum(modalData.midcourt,  1),
                defenders: safeNum(modalData.defenders, 1),
            };
            if (editMode) {
                response = await extraActionService.Update(currentId, payload);
            } else {
                response = await extraActionService.Add(payload);
            }

            if (response.success) {
                Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
                setOpenModal(false);
                setModalData({ title: "", shooters: 1, midcourt: 1, defenders: 1 });
                refetch();
            } else {
                Swal.fire({ icon: "error", text: handleServerValidations(response), ...SWAL_SETTINGS });
            }
        } catch (error) {
            Swal.fire({ icon: "error", text: handleServerValidations(error), ...SWAL_SETTINGS });
        }
    };

    const deleteRecord = (data) => {
        Swal.fire({
            customClass: "swal-wide",
            title: t("msg_are_you_sure"),
            text: t("you_not_be_able_to_revert_this"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
            cancelButtonColor: "#000",
            confirmButtonText: t("btn_yes_delete"),
            cancelButtonText: t("btn_cancel")
        }).then((result) => {
            if (result.isConfirmed) {
                extraActionService.Delete(data.originalId).then((response) => {
                    if (response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message,
                            ...SWAL_SETTINGS,
                        });
                        refetch();
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS,
                        });
                    }
                }).catch((error) => {
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(error),
                        ...SWAL_SETTINGS,
                    });
                });
            }
        });
    };

    const colDefs = useMemo(() => [
        {
            headerName: t("list_heading_sno"),
            field: "srNo",
            maxWidth: 85,
            sortable: false,
            filter: false,
            valueGetter: (params) => params.node.rowIndex + 1,
            cellStyle: { borderRight: "1px solid #dee2e6", color: "#3c4858" },
        },
        {
            headerName: "Title",
            field: "title",
            flex: 1,
            minWidth: 200,
            tooltipField: "title",
        },
        {
            headerName: "Slug",
            field: "slug",
            minWidth: 150,
        },
        {
            headerName: "Shooters (GS, GA)",
            field: "shooters",
            minWidth: 130,
            maxWidth: 160,
        },
        {
            headerName: "Midcourt (WA, C, WD)",
            field: "midcourt",
            minWidth: 150,
            maxWidth: 180,
        },
        {
            headerName: "Defenders (GD, GK)",
            field: "defenders",
            minWidth: 140,
            maxWidth: 170,
        },
        {
            headerName: t("list_heading_created_date"),
            field: "createdAt",
            filter: "agDateColumnFilter",
            minWidth: 150,
            maxWidth: 180,
            valueGetter: (params) => params.data?.createdAt ? new Date(params.data.createdAt) : null,
            valueFormatter: (params) => params.value ? moment(params.value).format("MMMM DD, YYYY") : ""
        },
        {
            headerName: t("list_heading_action"),
            maxWidth: 130,
            sortable: false,
            filter: false,
            cellRenderer: (params) => {
                const row = params.data;
                return (
                    <div className="btn-toolbar table-actions">
                        <div className="btn-group btn-group-sm">
                            <Whisper
                                placement="top"
                                trigger="hover"
                                speaker={<Tooltip style={{ fontWeight: 'bold' }}>Edit</Tooltip>}
                            >
                                <button
                                    type="button"
                                    className="btn edit"
                                    onClick={() => {
                                    setEditMode(true);
                                    setCurrentId(row.originalId);
                                    setModalData({
                                        title:     row.title,
                                        shooters:  row.shooters,
                                        midcourt:  row.midcourt,
                                        defenders: row.defenders,
                                    });
                                    setOpenModal(true);
                                }}
                                >
                                    <i className="ri-pencil-fill"></i>
                                </button>
                            </Whisper>

                            <Whisper
                                placement="top"
                                trigger="hover"
                                speaker={<Tooltip style={{ fontWeight: 'bold' }}>Delete</Tooltip>}
                            >
                                <button
                                    type="button"
                                    className="btn delete"
                                    onClick={() => deleteRecord(row)}
                                >
                                    <i className="ri-delete-bin-6-fill"></i>
                                </button>
                            </Whisper>
                        </div>
                    </div>
                );
            },
        },
    ], [t]);

    const rowData = useMemo(() => {
        if (!listData?.data) return [];
        return listData.data
            .map((row) => ({
                originalId: row.id,
                title:      row.title || "N/A",
                slug:       row.slug  || "N/A",
                // New records have flat top-level fields; old records fall back to legacy `points`
                shooters:   row.shooters  ?? row.points ?? 1,
                midcourt:   row.midcourt  ?? row.points ?? 1,
                defenders:  row.defenders ?? row.points ?? 1,
                createdAt:  row.createdAt,
            }))
            .filter((row) =>
                searchData
                    ? row.title.toLowerCase().includes(searchData.toLowerCase()) ||
                      row.slug.toLowerCase().includes(searchData.toLowerCase())
                    : true
            );
    }, [listData?.data, searchData]);



    const getAgGridConfig = useMemo(() => ({
        ref: gridRef,
        domLayout: "autoHeight",
        rowData: rowData,
        columnDefs: colDefs,
        noRowsOverlayComponent: NoRowsOverlay,
        pagination: true,
        paginationPageSize: 10,
        suppressCellFocus: true,
        defaultColDef: {
            resizable: true,
            sortable: true,
            minWidth: 150,
            cellStyle: { padding: "10px 8px" },
            suppressHeaderMenuButton: true,
        },
        headerHeight: 50,
        rowHeight: 45,
    }), [rowData, colDefs]);

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card overflow-hidden">
                    <div className="card-header d-flex justify-content-between align-items-center" style={{ borderBottom: "none" }}>
                        <h6 className="main-content-label">Extra Actions</h6>
                        <div className="d-flex align-items-center">
                            <div style={{ width: 300 }} className="me-3">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search..."
                                    value={searchData}
                                    onChange={(e) => setSearchData(e.target.value)}
                                    style={{ outline: "none", boxShadow: "none" }}
                                />
                            </div>
                            <button
                                className="btn ripple btn-main-primary signbtn"
                                onClick={() => {
                                    setEditMode(false);
                                    setModalData({ title: "", shooters: 1, midcourt: 1, defenders: 1 });
                                    setOpenModal(true);
                                }}
                            >
                                {t("btn_add_new")}
                            </button>
                        </div>
                    </div>

                    <div className="card-body">
                        {isLoading ? (
                            <Placeholder.Paragraph className="w-100 admin-placeholder" rowHeight={30} rowSpacing={5} active={true} rows={7} />
                        ) : (
                            <div className="ag-theme-alpine" style={{ width: '100%' }}>
                                <AgGridReact {...getAgGridConfig} />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Modal open={openModal} onClose={() => setOpenModal(false)} size="sm">
                <Modal.Header>
                    <Modal.Title>{editMode ? "Edit Extra Action" : "Add Extra Action"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form fluid>
                        <Form.Group controlId="title">
                            <Form.ControlLabel>Title</Form.ControlLabel>
                            <Form.Control
                                name="title"
                                value={modalData.title}
                                onChange={(val) => setModalData({ ...modalData, title: val })}
                            />
                        </Form.Group>

                        <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                            <Form.Group controlId="shooters" style={{ flex: 1 }}>
                                <Form.ControlLabel style={{ fontSize: 12 }}>Shooters (GS, GA)</Form.ControlLabel>
                                <Form.Control
                                    name="shooters"
                                    type="number"
                                    step="0.25"
                                    value={modalData.shooters}
                                    onChange={(val) => setModalData({ ...modalData, shooters: val })}
                                />
                            </Form.Group>
                            <Form.Group controlId="midcourt" style={{ flex: 1 }}>
                                <Form.ControlLabel style={{ fontSize: 12 }}>Midcourt (WA, C, WD)</Form.ControlLabel>
                                <Form.Control
                                    name="midcourt"
                                    type="number"
                                    step="0.25"
                                    value={modalData.midcourt}
                                    onChange={(val) => setModalData({ ...modalData, midcourt: val })}
                                />
                            </Form.Group>
                            <Form.Group controlId="defenders" style={{ flex: 1 }}>
                                <Form.ControlLabel style={{ fontSize: 12 }}>Defenders (GD, GK)</Form.ControlLabel>
                                <Form.Control
                                    name="defenders"
                                    type="number"
                                    step="0.25"
                                    value={modalData.defenders}
                                    onChange={(val) => setModalData({ ...modalData, defenders: val })}
                                />
                            </Form.Group>
                        </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleAddEdit} appearance="primary" style={{ backgroundColor: THEME_COLORS.ADMIN_PRIMARY }}>
                        {editMode ? t("btn_update") : t("btn_submit")}
                    </Button>
                    <Button onClick={() => setOpenModal(false)} appearance="subtle">
                        {t("btn_cancel")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ExtraActionList;
